import { Component, Injector, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Phrase } from '../../model/phrase';
import { Translation } from '../../model/translation';
import { ActionWindowService } from '../../service/action-window.service';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../service/language.service';
import { FormControlEx } from '../forms/form-control';
import { TranslateDialogFormComponent } from '../translate-dialog-form/translate-dialog-form.component';
import { InputControlBase } from './../forms/input-control.base';

@Component({
    selector: 'translate-text-box',
    templateUrl: './translate-text-box.component.html',
    styleUrls: ['./translate-text-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TranslateTextBoxComponent extends InputControlBase {
  private static nextId = 0;
  readonly inputId = `translate-text-${++TranslateTextBoxComponent.nextId}`;

  constructor(
    private _languageService: LanguageService,
    private _fcf: FormControlExFactory,
    private _injector: Injector,
    private _dialogService: ActionWindowService
  ) {
    super();
    this.subscription.add(
      this._languageService.onLangChange.subscribe(() => {
        this.textControl.setValue(this.phrase.currentTranslationText, { emitEvent: false });
        this.textControl.markAllAsTouched();
      })
    );
  }

  @Input() showTranslate = true;

  phrase: Phrase;

  private _initialText: string;
  private _textControl: FormControlEx;
  get textControl(): FormControlEx {
    return this._textControl || this.initializeTextControl();
  }

  private initializeTextControl(): FormControlEx {
    this._initialText = this.phrase.currentTranslationText;

    this.createNewTextControl();
    this.addTextControlToForm();
    this.setupTextControlSubscriptions();

    return this._textControl;
  }

  private createTextValidator(): ValidatorFn {
    const validator = this.showTranslate
      ? Validators.compose([
          this.control.validator,
          Translation.cannotRemove(this._initialText),
          Translation.requireTranslation(this.phrase, { source: this.control }),
        ])
      : this.control.validator;
    return validator;
  }

  private createNewTextControl(): void {
    this._textControl = this._fcf.createWithOptions(this.control.caption, this._initialText, {
      isReadonly: this.control.isReadonly,
      asyncValidator: this.control.asyncValidator,
      helpText: this.control.helpText as string,
      validator: this.createTextValidator(),
      noSubmit: true,
    });
  }

  // This is done for the sake of onSubmit validation.
  private addTextControlToForm() {
    const group = this.control.parent as FormGroup;
    if (!group.controls || !group.addControl) {
      console.error('Unable to register current translation controller. Parent is not FormGroup.');
    }

    // Find unqiue name:
    let i: number = 0;
    let name: string;
    do {
      name = 'TranslationText_' + ++i;
    } while (group.controls[name]);

    group.addControl(name, this._textControl, { emitEvent: false });
  }

  private setupTextControlSubscriptions(): void {
    // Propagate value changes from text control to data sources
    this.subscription.add(
      this._textControl.valueChanges.subscribe(val => {
        this.phrase.setCurrentTranslation(val);
        this.control.setValue(this.phrase.translations.filter(t => t.translationText));
        this.control.parent.markAsDirty();
      })
    );

    // Propagate touch from the hidden source control to text control (for showing validation errors)
    this.subscription.add(
      this.control.touchStateChanged.subscribe(touched => {
        touched ? this._textControl.markAsTouched() : this._textControl.markAsUntouched();
        this._textControl.updateValueAndValidity({ onlySelf: true });
      })
    );

    // Propagate validator updates (to capture 'required' changes)
    this.subscription.add(
      this.control.validatorsChanged.subscribe(() => {
        this._textControl.setValidators(this.createTextValidator());
        this._textControl.updateValueAndValidity({ onlySelf: true });
      })
    );
  }

  // Propagate touch from text control to the hidden source control (for validation logic when form is empty)
  onblur() {
    super.onblur();
    this.control.markAsTouched();
  }

  onControlSet() {
    super.onControlSet();
    this.phrase = new Phrase(this.control.value, this._languageService);
  }

  private translateSub: Subscription;
  translateClicked(): boolean {
    if (this.translateSub) {
      this.translateSub.unsubscribe();
    }
    this.translateSub = this._dialogService
      .open(TranslateDialogFormComponent, this._injector, null, { phrase: this.phrase, validator: this.control.validator })
      .subscribe((result: Phrase) => {
        this.phrase = result;

        // assign current-language translation to the text control (visible on the form).
        this.textControl.setValue(this.phrase.currentTranslationText);
        // assign all non-empty translations back to the source control (which is hidden).
        this.control.setValue(result.translations.filter(t => t.translationText));

        this.control.markAsDirty();
        this.textControl.markAsDirty();
        this.translateSub.unsubscribe(); // unsubscribe from translation box
      });

    return false;
  }

  get errorMessage(): { message: string; params: any } {
    if (this.textControl.invalid) {
      const error = this.textControl.getErrorMessage();
      if (!error?.message) {
        console.warn(error);
        throw new Error('Error message was not specified.');
      }
      return error;
    }
    return null;
  }

  get paddingClass(): any {
    return { translatePadding: this.showTranslate };
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.translateSub) {
      this.translateSub.unsubscribe();
    }
  }
}
